import React, { useState, useEffect, useRef, useCallback } from 'react';

function getAuthHeaders() {
  const u = JSON.parse(localStorage.getItem('user') || '{}');
  return { Authorization: `Bearer ${u.token}` };
}

function elapsed(clockIn) {
  const ms  = Date.now() - new Date(clockIn).getTime();
  if (ms < 0) return '00:00';
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function fmtHours(h) {
  return parseFloat(h || 0).toFixed(2);
}

export default function ClockBoard() {
  const [active, setActive]   = useState([]);   // currently clocked in
  const [history, setHistory] = useState([]);   // recent clock-outs
  const [tab, setTab]         = useState('live');
  const [tick, setTick]       = useState(0);    // forces elapsed re-render every minute
  const intervalRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [teamRes, histRes] = await Promise.all([
        fetch('/api/clock/team',    { headers: getAuthHeaders() }),
        fetch('/api/clock/history?limit=30', { headers: getAuthHeaders() }),
      ]);
      if (teamRes.ok) setActive(await teamRes.json());
      if (histRes.ok) setHistory(await histRes.json());
    } catch (e) { console.error('ClockBoard fetch error:', e); }
  }, []);

  useEffect(() => {
    fetchData();
    // Refresh live data every 30 seconds
    intervalRef.current = setInterval(() => {
      fetchData();
      setTick(t => t + 1);
    }, 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchData]);

  // Update elapsed display every minute without re-fetching
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const badge = (count, color) => count > 0 ? (
    <span style={{
      marginLeft: 6, minWidth: 20, height: 20,
      background: color, color: 'white',
      borderRadius: 99, fontSize: '0.7rem', fontWeight: 800,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      padding: '0 6px',
    }}>{count}</span>
  ) : null;

  return (
    <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden', marginBottom: '1.5rem' }}>

      {/* Header */}
      <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: active.length > 0 ? '#10b981' : '#d1d5db', display: 'inline-block', boxShadow: active.length > 0 ? '0 0 0 3px rgba(16,185,129,0.2)' : 'none' }} />
          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
            Team Clock Status
          </span>
          {active.length > 0 && (
            <span style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 600 }}>
              {active.length} working now
            </span>
          )}
        </div>
        <button
          onClick={fetchData}
          style={{ fontSize: '0.75rem', color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #f3f4f6' }}>
        {[['live', 'Clocked In'], ['history', 'Recent Clock-Outs']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              flex: 1, padding: '0.65rem 0', fontSize: '0.8rem', fontWeight: 600,
              border: 'none', background: 'none', cursor: 'pointer',
              color: tab === key ? '#22c55e' : '#6b7280',
              borderBottom: tab === key ? '2px solid #22c55e' : '2px solid transparent',
            }}
          >
            {label}
            {key === 'live'    && badge(active.length,  '#10b981')}
            {key === 'history' && badge(history.length, '#6b7280')}
          </button>
        ))}
      </div>

      {/* Live tab */}
      {tab === 'live' && (
        <div style={{ minHeight: 80 }}>
          {active.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.875rem' }}>
              No one is currently clocked in.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Employee', 'Clocked In', 'Elapsed'].map(h => (
                    <th key={h} style={{ padding: '0.6rem 1rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {active.map((s, i) => (
                  <tr key={s._id} style={{ borderTop: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #10b981, #059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>
                          {s.employee?.firstName?.[0]}{s.employee?.lastName?.[0]}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#111827' }}>
                            {s.employee ? `${s.employee.firstName} ${s.employee.lastName}` : 'Unknown'}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#9ca3af', textTransform: 'capitalize' }}>
                            {s.employee?.role || ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#374151' }}>
                      {fmtTime(s.clockIn)}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{ background: '#d1fae5', color: '#065f46', padding: '3px 10px', borderRadius: 99, fontSize: '0.78rem', fontWeight: 700 }}>
                        {elapsed(s.clockIn)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* History tab */}
      {tab === 'history' && (
        <div style={{ minHeight: 80 }}>
          {history.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.875rem' }}>
              No recent clock-outs.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Employee', 'Date', 'In', 'Out', 'Hours'].map(h => (
                    <th key={h} style={{ padding: '0.6rem 1rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((s, i) => {
                  const hrs = s.clockOut
                    ? fmtHours((new Date(s.clockOut) - new Date(s.clockIn)) / 3600000)
                    : '—';
                  return (
                    <tr key={s._id} style={{ borderTop: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                      <td style={{ padding: '0.75rem 1rem', fontWeight: 600, fontSize: '0.875rem', color: '#111827' }}>
                        {s.employee ? `${s.employee.firstName} ${s.employee.lastName}` : 'Unknown'}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: '#6b7280' }}>
                        {new Date(s.clockIn).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: '#374151' }}>
                        {fmtTime(s.clockIn)}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: '#374151' }}>
                        {s.clockOut ? fmtTime(s.clockOut) : '—'}
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <span style={{ background: '#e0e7ff', color: '#3730a3', padding: '3px 10px', borderRadius: 99, fontSize: '0.78rem', fontWeight: 700 }}>
                          {hrs}h
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
